import axios from "axios";
import { api } from "../api";
import { mockGuard } from "../mocks/mockGuard";
import {
  mockOverviewMetrics,
  mockSiteMetrics,
  mockTagsMetrics,
  mockSalesFunnelMetrics,
  mockGoogleAdsMetrics,
  mockMetaAdsMetrics,
  mockShowcaseMetrics,
  mockAllTagTrafficMetrics,
} from "../mocks/mockMetrics";
import { mockAdsBreakdown } from "../mocks/mockAdsBreakdown";
import { ReportQuery, toApiRange } from "@/utils/period";
import { AdsPlatform, BreakdownParams, BreakdownResponse } from "@/types/adsBreakdown";
import {
  GoogleAdsReport,
  MetaAdsReport,
  OverviewReport,
  SalesFunnelReport,
  ShowcaseReport,
  SiteReport,
  TagTrafficPage,
  TagTrafficRow,
  TagsReport,
} from "@/types/reports";

// Requisições de relatório em andamento, por endpoint + período. Chamadas
// idênticas simultâneas (ex.: o StrictMode monta o efeito duas vezes em dev,
// ou dois cards pedem o mesmo relatório) compartilham um único request.
const pendingReports = new Map<string, Promise<unknown>>();

// Todos os relatórios seguem o mesmo contrato: GET /report/<nome> com
// start/end no fuso de São Paulo, resposta em { data }.
const fetchReport = <T>(path: string) => (query: ReportQuery): Promise<T> => {
  const params = toApiRange(query);
  const key = `${path}?${JSON.stringify(params)}`;
  const pending = pendingReports.get(key);
  if (pending) return pending as Promise<T>;

  const request = api
    .get<{ data: T }>(`/report/${path}`, { params })
    .then((response) => response.data.data)
    .finally(() => pendingReports.delete(key));
  pendingReports.set(key, request);
  return request;
};

// Chamado com axios direto (sem o interceptor), como no fluxo original: a
// listagem pagina muitas vezes e não deve disparar toasts/redirecionamentos.
const fetchTagTrafficPage = async (
  query: ReportQuery,
  trafficSource: string,
  page: number,
): Promise<TagTrafficPage> => {
  const response = await axios.get(`${api.defaults.baseURL}/report/tag-traffic-metrics`, {
    params: { ...toApiRange(query), trafficSource, page },
    withCredentials: true,
  });
  const body = response.data;
  return Array.isArray(body)
    ? { data: body, pagination: { page, total: 1 } }
    : { data: body?.data ?? [], pagination: body?.pagination ?? { page, total: 1 } };
};

// Uma página de tags de um canal (tabela paginada igual ao endpoint). Pedidos
// iguais simultâneos compartilham a mesma requisição.
const pendingTagTrafficPages = new Map<string, Promise<TagTrafficPage>>();

const fetchTagTrafficMetricsPageReal = (query: ReportQuery, trafficSource: string, page: number) => {
  const key = `${trafficSource}?${JSON.stringify(toApiRange(query))}&page=${page}`;
  const pending = pendingTagTrafficPages.get(key);
  if (pending) return pending;

  const request = fetchTagTrafficPage(query, trafficSource, page).finally(() =>
    pendingTagTrafficPages.delete(key),
  );
  pendingTagTrafficPages.set(key, request);
  return request;
};

// Linha de uma tag no canal, para o detalhe da tag. Busca primeiro a página
// indicada (a da lista de onde a pessoa veio); se a tag não estiver nela
// (link direto, período trocado), procura página a página e para ao achar,
// em vez de buscar todas.
const fetchTagTrafficRowReal = async (
  query: ReportQuery,
  trafficSource: string,
  tag: string,
  hintPage = 1,
): Promise<TagTrafficRow | null> => {
  const first = await fetchTagTrafficMetricsPageReal(query, trafficSource, hintPage);
  const found = first.data.find((row) => row.tag === tag);
  if (found) return found;

  const totalPages = Number(first.pagination.total || 1);
  for (let page = 1; page <= totalPages; page += 1) {
    if (page === hintPage) continue;
    const { data } = await fetchTagTrafficMetricsPageReal(query, trafficSource, page);
    const row = data.find((item) => item.tag === tag);
    if (row) return row;
  }
  return null;
};

// Breakdown de anúncios (Meta/Google) por nível. Arrays viram parâmetros
// repetidos (?status=A&status=B) e campos vazios não são enviados — um
// `?status=` vazio filtraria por status vazio.
const toBreakdownSearch = (query: ReportQuery, params: BreakdownParams) => {
  const search = new URLSearchParams(toApiRange(query));
  search.set("level", params.level);
  search.set("page", String(params.page));
  search.set("limit", String(params.limit));
  search.set("sortBy", params.sortBy);
  search.set("sortDir", params.sortDir);
  if (params.search) search.set("search", params.search);
  if (params.includeNoDelivery) search.set("includeNoDelivery", "true");
  (["status", "objective", "adAccountId", "customerId", "channelType"] as const).forEach((key) =>
    params[key]?.forEach((value) => search.append(key, value)),
  );
  Object.entries(params.ranges ?? {}).forEach(([key, value]) => search.set(key, String(value)));
  return search.toString();
};

const pendingBreakdowns = new Map<string, Promise<BreakdownResponse>>();

const fetchAdsBreakdownReal = (
  platform: AdsPlatform,
  query: ReportQuery,
  params: BreakdownParams,
): Promise<BreakdownResponse> => {
  const url = `/report/${platform}-ads-breakdown?${toBreakdownSearch(query, params)}`;
  const pending = pendingBreakdowns.get(url);
  if (pending) return pending;

  const request = api
    .get<BreakdownResponse>(url)
    .then((response) => response.data)
    .finally(() => pendingBreakdowns.delete(url));
  pendingBreakdowns.set(url, request);
  return request;
};

export const fetchAdsBreakdown = mockGuard(fetchAdsBreakdownReal, mockAdsBreakdown);

export const fetchHitsBillingCycleMetrics = async () => {
  const response = await api.get("/report/billing-cycle-metrics");
  return response.data;
};

// Na conta Preview, os relatórios abaixo devolvem dados fictícios sem chamar
// a API (ver src/service/mocks); fora dela, chamam a API normalmente.
const unwrap =
  <T>(mock: (period: ReportQuery["period"], ...rest: any[]) => { data: unknown }) =>
  (query: ReportQuery, ...rest: any[]) =>
    mock(query.period, ...rest).data as T;

export const fetchOverviewMetrics = mockGuard(
  fetchReport<OverviewReport>("overview-metrics"),
  unwrap<OverviewReport>(mockOverviewMetrics),
);
export const fetchSiteMetrics = mockGuard(
  fetchReport<SiteReport>("site-metrics"),
  unwrap<SiteReport>(mockSiteMetrics),
);
export const fetchTagsMetrics = mockGuard(
  fetchReport<TagsReport>("tags-metrics"),
  unwrap<TagsReport>(mockTagsMetrics),
);
export const fetchSalesFunnelMetrics = mockGuard(
  fetchReport<SalesFunnelReport>("sales-funnel-metrics"),
  unwrap<SalesFunnelReport>(mockSalesFunnelMetrics),
);
export const fetchGoogleAdsMetrics = mockGuard(
  fetchReport<GoogleAdsReport>("google-ads-metrics"),
  unwrap<GoogleAdsReport>(mockGoogleAdsMetrics),
);
export const fetchMetaAdsMetrics = mockGuard(
  fetchReport<MetaAdsReport>("meta-ads-metrics"),
  unwrap<MetaAdsReport>(mockMetaAdsMetrics),
);
export const fetchShowcaseMetrics = mockGuard(
  fetchReport<ShowcaseReport>("showcase-metrics"),
  unwrap<ShowcaseReport>(mockShowcaseMetrics),
);
export const fetchTagTrafficRow = mockGuard(
  fetchTagTrafficRowReal,
  (query: ReportQuery, trafficSource: string, tag: string) =>
    (mockAllTagTrafficMetrics(query.period, trafficSource).data as TagTrafficRow[]).find((row) => row.tag === tag) ??
    null,
);
export const fetchTagTrafficMetricsPage = mockGuard(
  fetchTagTrafficMetricsPageReal,
  (query: ReportQuery, trafficSource: string, _page: number) =>
    mockAllTagTrafficMetrics(query.period, trafficSource) as TagTrafficPage,
);
